/**
 * `renderer.compileAsync` with a guard for materials disposed while their program is still compiling.
 * three's own poll reads `properties.get(m).currentProgram.isReady()` and throws
 * «reading 'isReady'» from a setTimeout when the owner unmounted mid-compile (its promise then never settles).
 * Disposed or program-less materials are dropped from the wait set instead. Resolves with `object`.
 */
export function compileAsyncSafe(renderer, object, camera, scene = null) {
  const materials = renderer.compile(object, camera, scene);
  const parallel = renderer.extensions?.has?.('KHR_parallel_shader_compile') === true;
  return new Promise((resolve) => {
    const check = () => {
      for (const m of materials) {
        const program = renderer.properties.has(m) ? renderer.properties.get(m).currentProgram : undefined;
        if (!program || program.isReady()) materials.delete(m);
      }
      if (materials.size === 0) { resolve(object); return; }
      setTimeout(check, 10);
    };
    if (parallel) check(); else setTimeout(check, 10);
  });
}

/** compileAsync selects programs synchronously; restore render state before its promise settles. */
export function compileForTarget(renderer, object, camera, scene, target) {
  const previous = renderer.getRenderTarget();
  const face = renderer.getActiveCubeFace();
  const mip = renderer.getActiveMipmapLevel();
  try {
    renderer.setRenderTarget(target);
    return compileAsyncSafe(renderer, object, camera, scene);
  } finally {
    renderer.setRenderTarget(previous, face, mip);
  }
}
